import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useGetAllCategoriesQuery, useDeleteCategoryMutation } from '../../services/productApi'
import { Add, CategoryOutlined } from "@mui/icons-material";
import {
  AdminPageHeader,
  AdminToolbar,
  AdminTable,
  AdminBadge,
  AdminButton,
  AdminRowActions,
  useConfirm,
  useToast,
} from '../../components/ui'

function Categories() {
  const { data, isLoading } = useGetAllCategoriesQuery()
  const [deleteCategory] = useDeleteCategoryMutation()
  const navigate = useNavigate();
  const confirm = useConfirm();
  const toast = useToast();
  const [search, setSearch] = useState("");

  const allRows = data ?? []
  const categoryById = Object.fromEntries(allRows.map((c) => [c.id, c.name]))
  const term = search.trim().toLowerCase();
  const rows = term
    ? allRows.filter((c) => c.name.toLowerCase().includes(term) || (c.description || "").toLowerCase().includes(term))
    : allRows;

  const handleEdit = (props) => {
    navigate(`/admin/category/edit/${props}`);
  };

  const handleDelete = async (category) => {
    const ok = await confirm({
      title: "Delete category",
      message: `Delete "${category.name}"? This can't be undone.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    const res = await deleteCategory(category.id)
    if (res.error) {
      toast.error(res.error.data?.errors?.category?.[0] || 'Could not delete this category.')
    } else {
      toast.success("Category deleted.")
    }
  }

  const columns = [
    { key: "id", header: "ID", width: 70 },
    { key: "name", header: "Name" },
    { key: "parent", header: "Parent Category", render: (c) => (c.parent ? categoryById[c.parent] : "—") },
    {
      key: "description",
      header: "Description",
      render: (c) => <span className="admin-table__truncate" title={c.description}>{c.description || "—"}</span>,
    },
    { key: "is_active", header: "Status", render: (c) => <AdminBadge variant={c.is_active ? "success" : "neutral"}>{c.is_active ? "Active" : "Inactive"}</AdminBadge> },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (c) => <AdminRowActions label={c.name} onEdit={() => handleEdit(c.id)} onDelete={() => handleDelete(c)} />,
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Categories"
        subtitle="Organize products into categories."
        breadcrumbs={[{ label: "Dashboard", to: "/admin/dashboard" }, { label: "Categories" }]}
        primaryAction={
          <Link to="/admin/category/new">
            <AdminButton icon={<Add />}>New Category</AdminButton>
          </Link>
        }
      />
      <AdminToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search categories by name or description..." />
      <AdminTable
        columns={columns}
        rows={rows}
        rowKey={(c) => c.id}
        isLoading={isLoading}
        emptyIcon={<CategoryOutlined />}
        emptyTitle={search ? "No categories match your search" : "No categories yet"}
        pageSize={10}
      />
    </div>
  );
}

export default Categories
